import {
    getSafetyEventFromString,
    SignalDataEndpoint,
    SignalsEndpoints,
} from "components/Signals/SignalsModels";
import type { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import {
    DrilldownBehavior,
    DrilldownSelection,
    DrilldownSource,
    ZoomPathEntry,
} from "../Drilldown/DrilldownTypes";
import { HipaaCompliance } from "stores/ApplicationFilters/Filters/HipaaCompliance";
import { CallDirection } from "stores/ApplicationFilters/Filters/CallDirection";
import { MediaType } from "stores/ApplicationFilters/Filters/MediaType";
import { EvaluationType } from "stores/ApplicationFilters/Filters/EvaluationType";
import { PatientJourneyStage } from "stores/ApplicationFilters/Filters/PatientJourneyStage";
import { getPublishedModelResultsClassifierIdFromEndpoint } from "./DefaultSelectionsSetters";

// Drilldown strategies get merged into the config for each data endpoint, they are kept here to keep the file size manageable

const resolveJourneyStage = (
    label: string | number | null | undefined,
): PatientJourneyStage | undefined => {
    switch (label) {
        case "Before Treatment":
            return PatientJourneyStage.BeforeTreatment;
        case "On Treatment":
            return PatientJourneyStage.OnTreatment;
        case "After Treatment":
            return PatientJourneyStage.AfterTreatment;
        case "Restarting Treatment":
            return PatientJourneyStage.RestartingTreatment;
        case "Treatment Disruption":
            return PatientJourneyStage.TreatmentDisruption;
        case "No Clear Journey Stage":
            return PatientJourneyStage.NoClearJourneyStage;
    }
};

const resolveSafetyEventDrilldownValue = (
    label: string | null | undefined,
):
    | "Identified"
    | "NotIdentified"
    | "Acknowledged"
    | "NotAcknowledged"
    | undefined => {
    switch (label) {
        case "Identified":
            return "Identified";
        case "Not Identified":
            return "NotIdentified";
        case "Acknowledged":
            return "Acknowledged";
        case "Not Acknowledged":
            return "NotAcknowledged";
        default:
            return;
    }
};

const getIdentifierValue = (
    identifier: string | undefined,
): string | undefined => {
    const value = identifier?.split("=:=")[0]?.trim();
    return value || undefined;
};

// Grouped model-results charts render one bar per classifier, so the clicked classifier id lives
// on the datum (x_identifier) rather than on a chart-wide filter. The group can hold both LLM models
// and Lucene (RBC) classifiers, so the filter we apply is type-aware:
//   - LLM model  -> Model Results filter scoped to the clicked result category.
//   - Lucene/RBC -> RBC filter whose "contains" flag mirrors the clicked True/False stack.
// A classifier is treated as an LLM model when it is present in the org's published LLM classifiers
// (the same signal used to source the KBo "…for {model}" selector). Shared by the interactive and
// published variants since the classifier comes from the datum either way.
const groupedModelResultsDrilldown: DrilldownBehavior = {
    needs: ["data"],
    applyToRequestState: ({ selection, state, source }) => {
        const datum = source.data?.find(
            (v) => v.x === selection.x && v.grouping === selection.series,
        );
        if (!datum?.x_identifier) return;
        // The clicked classifier id — LLM model or Lucene/RBC. The grouped drilldown endpoint filters
        // by ClassifierId through this field (named llmClassifierId for historical reasons), so it
        // resolves the drilldown for any classifier type.
        state.llmClassifierId = datum.x_identifier;
        state.result = selection.series;
    },
    applyToFilters: ({ selection, filters, source, optionsStore }) => {
        const datum = source.data?.find(
            (v) => v.x === selection.x && v.grouping === selection.series,
        );
        if (!datum?.x_identifier || !selection.series) return;
        const classifierId = datum.x_identifier;
        const llmClassifier = optionsStore.llmClassifiers.find(
            (i) => i.id === classifierId,
        );

        if (llmClassifier) {
            const existing = filters.modelResults ?? [];
            filters.modelResults = [
                ...existing,
                {
                    id: classifierId,
                    name: llmClassifier.name ?? String(selection.x ?? ""),
                    results: [selection.series],
                },
            ];
            return;
        }

        // Lucene/RBC classifier: stacks are the True/False result. IRBCFilter.value is the
        // "contains" flag, so clicking the True stack -> contains, the False stack -> does-not-contain.
        const contains =
            String(selection.series).trim().toLowerCase() === "true";
        const existing = filters.rbcFilterItems ?? [];
        filters.rbcFilterItems = [
            ...existing,
            {
                value: contains,
                classifierIds: [classifierId],
                combinator: 0,
                filterGroupId: 0,
            },
        ];
    },
};

const getMetadataValue = (
    metadata: { name: string; value: unknown }[] | undefined,
    name: string,
): unknown => metadata?.find((item) => item.name === name)?.value;

const getMetadataString = (
    metadata: { name: string; value: unknown }[] | undefined,
    name: string,
): string | undefined => {
    const value = getMetadataValue(metadata, name);
    return typeof value === "string" && value.length ? value : undefined;
};

const parseTreeMapNumber = (
    identifier: string | undefined,
): number | undefined => {
    const value = Number(getIdentifierValue(identifier));
    return Number.isFinite(value) ? value : undefined;
};

const getMetadataNumber = (
    metadata: { name: string; value: unknown }[] | undefined,
    name: string,
): number | undefined => {
    const value = getMetadataValue(metadata, name);
    const number = typeof value === "number" ? value : Number(value);
    return Number.isFinite(number) ? number : undefined;
};

const getTopTwentyThemesThemeNumber = (
    selection: DrilldownSelection,
    zoomPath: ZoomPathEntry[],
): number | undefined =>
    zoomPath.length > 0
        ? parseTreeMapNumber(zoomPath[0].xIdentifier)
        : parseTreeMapNumber(selection.xIdentifier);

const getTopTwentyThemesDriverIndex = (
    selection: DrilldownSelection,
    zoomPath: ZoomPathEntry[],
): number | undefined => {
    if (zoomPath.length > 1) {
        return (
            getMetadataNumber(zoomPath[1].metadata, "driverIndex") ??
            parseTreeMapNumber(zoomPath[1].xIdentifier)
        );
    }
    if (zoomPath.length === 1) {
        return (
            getMetadataNumber(selection.metadata, "driverIndex") ??
            parseTreeMapNumber(selection.xIdentifier)
        );
    }
    return undefined;
};

const getTopTwentyThemesThemeMetadata = (
    selection: DrilldownSelection,
    zoomPath: ZoomPathEntry[],
): { name: string; value: unknown }[] | undefined =>
    zoomPath.length > 0 ? zoomPath[0].metadata : selection.metadata;

const getTopTwentyThemesThemeLabel = (
    selection: DrilldownSelection,
    zoomPath: ZoomPathEntry[],
): string | undefined => {
    if (zoomPath.length > 0) return zoomPath[0].x;
    if (typeof selection.x === "string") return selection.x;
    return selection.xLabel;
};

const getTopTwentyThemesDriverLabel = (
    selection: DrilldownSelection,
    zoomPath: ZoomPathEntry[],
): string | undefined => {
    if (zoomPath.length > 1) return zoomPath[1].x;
    if (zoomPath.length === 1) {
        return typeof selection.x === "string" ? selection.x : selection.xLabel;
    }
    return undefined;
};

const applyDriverDerivedThemeDrilldownFilter = (
    metadata: { name: string; value: unknown }[] | undefined,
    label: string | undefined,
    filters: ApplicationFilters,
): void => {
    const themeId = getMetadataString(metadata, "themeId");
    if (!themeId || !label) return;
    filters.themes = [{ id: themeId, themeLabel: label }];
    filters.containsThemes = true;
};

const applyDriverDrilldownFilter = (
    driverIndex: number | undefined,
    driverName: string | undefined,
    themeNumber: number | undefined,
    filters: ApplicationFilters,
): void => {
    if (driverIndex === undefined || !driverName) return;
    filters.drivers = [{ driverIndex, driverName, themeNumber }];
    filters.containsDrivers = true;
};

const getThemesAndTopDriversDatum = (
    selection: DrilldownSelection,
    data: unknown,
):
    | {
          metadata?: { name: string; value: unknown }[];
          grouping_identifier?: string | number | null;
      }
    | undefined => {
    if (!Array.isArray(data)) return;
    return data.find(
        (datum) =>
            datum?.x === selection.x && datum?.grouping === selection.series,
    );
};

/**
 * The Eddy option an Avg Call Duration card's title selector is showing, read off the card's own
 * request state. The property is named for the backend field that state feeds (`EddyPresence` on
 * SignalsStatAvgCallDurationRequest), which is also how the option is seeded.
 */
const resolveEddyPresence = (
    source: DrilldownSource,
): string | undefined => {
    const value = source.state?.EddyPresence;
    return typeof value === "string" ? value : undefined;
};

export const drilldownStrategies: Partial<
    Record<SignalDataEndpoint, DrilldownBehavior>
> = {
    [SignalsEndpoints.legacyTopicReports.TrendingTopicsID]: {
        applyToFilters: ({ selectedTopic, filters }) => {
            if (!selectedTopic) return;
            filters.primaryTopics = [
                {
                    topicNumber: selectedTopic.topicNumber,
                    topicLabel: selectedTopic.selectedTopic,
                },
            ];
            filters.containsPrimaryTopics = true;
        },
    },
    [SignalsEndpoints.legacyTopicReports.TopicsOnTheRiseID]: {
        drilldownViewDataEndpoint:
            SignalsEndpoints.legacyTopicReports.TrendingTopicsID,
        drilldownViewTopicsEndpoint:
            SignalsEndpoints.legacyTopicReports.TrendingTopicsID,
        applyToFilters: ({ selectedTopic, filters }) => {
            if (!selectedTopic) return;
            filters.primaryTopics = [
                {
                    topicNumber: selectedTopic.topicNumber,
                    topicLabel: selectedTopic.selectedTopic,
                },
            ];
            filters.containsPrimaryTopics = true;
        },
    },
    // The two Driver Cards resolve a SelectedDriver — the same selection the Theme Cards' View
    // Drivers flow produces — so both read it straight off the strategy context. Neither has a zoom
    // path, so there is no zoomPath handling here.
    [SignalsEndpoints.drivers.TrendingDriversID]: {
        applyToRequestState: ({ state, selectedDriver }) => {
            state.driverIndex = selectedDriver?.driverIndex;
        },
        applyToFilters: ({ filters, selectedDriver }) => {
            applyDriverDrilldownFilter(
                selectedDriver?.driverIndex,
                selectedDriver?.selectedDriver,
                selectedDriver?.themeNumber,
                filters,
            );
        },
    },
    [SignalsEndpoints.drivers.DriversOnTheRiseID]: {
        // View Data reuses the Trending Drivers drilldown rather than defining a second contract —
        // the same relationship TopicsOnTheRise has with TrendingTopics.
        drilldownViewDataEndpoint: SignalsEndpoints.drivers.TrendingDriversID,
        applyToRequestState: ({ state, selectedDriver }) => {
            state.driverIndex = selectedDriver?.driverIndex;
        },
        applyToFilters: ({ filters, selectedDriver }) => {
            applyDriverDrilldownFilter(
                selectedDriver?.driverIndex,
                selectedDriver?.selectedDriver,
                selectedDriver?.themeNumber,
                filters,
            );
        },
    },
    [SignalsEndpoints.drivers.TopTwentyThemesID]: {
        applyToRequestState: ({ selection, source, state }) => {
            const zoomPath = source.zoomPath ?? [];
            state.themeNumber = getTopTwentyThemesThemeNumber(selection, zoomPath);
            state.driverIndex = getTopTwentyThemesDriverIndex(selection, zoomPath);
        },
        applyToFilters: ({ selection, source, filters }) => {
            const zoomPath = source.zoomPath ?? [];
            const themeMetadata = getTopTwentyThemesThemeMetadata(
                selection,
                zoomPath,
            );
            applyDriverDerivedThemeDrilldownFilter(
                themeMetadata,
                getTopTwentyThemesThemeLabel(selection, zoomPath),
                filters,
            );
            applyDriverDrilldownFilter(
                getTopTwentyThemesDriverIndex(selection, zoomPath),
                getTopTwentyThemesDriverLabel(selection, zoomPath),
                getTopTwentyThemesThemeNumber(selection, zoomPath),
                filters,
            );
        },
        applyZoomEntryToFilters: ({ entry, filters }) => {
            if (entry.depth === 0) {
                applyDriverDerivedThemeDrilldownFilter(
                    entry.metadata,
                    entry.x,
                    filters,
                );
                return;
            }

            if (entry.depth === 1) {
                applyDriverDrilldownFilter(
                    getMetadataNumber(entry.metadata, "driverIndex") ??
                        parseTreeMapNumber(entry.xIdentifier),
                    getMetadataString(entry.metadata, "driverName") ?? entry.x,
                    getMetadataNumber(entry.metadata, "themeNumber"),
                    filters,
                );
            }
        },
    },
    [SignalsEndpoints.drivers.ThemesAndTopDriversID]: {
        needs: ["data"],
        applyToRequestState: ({ selection, source, state }) => {
            const datum = getThemesAndTopDriversDatum(selection, source.data);
            state.themeNumber = getMetadataNumber(
                datum?.metadata,
                "themeNumber",
            );
            state.driverIndex =
                getMetadataNumber(datum?.metadata, "driverIndex") ??
                parseTreeMapNumber(
                    datum?.grouping_identifier?.toString() ??
                        selection.seriesIdentifier,
                );
        },
        applyToFilters: ({ selection, source, filters }) => {
            const datum = getThemesAndTopDriversDatum(selection, source.data);
            const themeNumber = getMetadataNumber(
                datum?.metadata,
                "themeNumber",
            );
            const driverIndex =
                getMetadataNumber(datum?.metadata, "driverIndex") ??
                parseTreeMapNumber(
                    datum?.grouping_identifier?.toString() ??
                        selection.seriesIdentifier,
                );

            applyDriverDerivedThemeDrilldownFilter(
                datum?.metadata,
                typeof selection.x === "string" ? selection.x : undefined,
                filters,
            );
            applyDriverDrilldownFilter(
                driverIndex,
                getMetadataString(datum?.metadata, "driverName") ??
                    selection.series ??
                    undefined,
                themeNumber,
                filters,
            );
        },
    },
    [SignalsEndpoints.eddyReports.EEByClassifierID]: {
        applyToRequestState: ({ selection, state, optionsStore }) => {
            const classifierId = optionsStore.userClassifiers.find(
                (c) => c.name === selection.x,
            )?.id;
            state.classifierId = classifierId;
            delete (state as any).classifierIds;
        },
        applyToFilters: ({ selection, filters, optionsStore }) => {
            const selectedClassifierId = optionsStore.userClassifiers.find(
                (c) => c.name === selection.x,
            )?.id;
            filters.eddyEffectStatus = true;
            return { selectedClassifierId };
        },
    },
    [SignalsEndpoints.eddyReports.EEOverTimeID]: {
        applyToFilters: ({ filters }) => {
            filters.eddyEffectStatus = true;
        },
    },
    [SignalsEndpoints.eddyReports.EEByClassifiersOverTimeID]: {
        applyToRequestState: ({ selection, state, optionsStore }) => {
            const classifierId = optionsStore.userClassifiers.find(
                (c) => c.name === selection.series,
            )?.id;
            state.classifierId = classifierId;
            delete (state as any).classifierIds;
        },
        applyToFilters: ({ selection, filters, optionsStore }) => {
            const selectedClassifierId = optionsStore.userClassifiers.find(
                (c) => c.name === selection.series,
            )?.id;
            filters.eddyEffectStatus = true;
            return { selectedClassifierId };
        },
    },
    [SignalsEndpoints.eddyReports.EEPrevalenceByQuestionID]: {
        needs: ["selectedIds.question", "selectedIds.module"],
        applyToRequestState: ({ selection, state, source, optionsStore }) => {
            const selectedQuestionId = source.selectedIds?.question;
            const selectedQuestion = optionsStore.orgModules
                .find((module) => module.id === source.selectedIds?.["module"])
                ?.questions.find((q) => q.id === selectedQuestionId);
            const selectedTagId = selectedQuestion?.tags?.find(
                (t) => t.value === selection.x,
            )?.id;

            state.tagId = selectedTagId;
        },
    },
    [SignalsEndpoints.additionalReports.CountByQuestionResponseId]: {
        needs: ["selectedIds.question", "selectedIds.module"],
        applyToRequestState: ({ selection, state, source, optionsStore }) => {
            const selectedQuestionId = source.selectedIds?.question;
            const selectedQuestion = optionsStore.orgModules
                .find((module) => module.id === source.selectedIds?.["module"])
                ?.questions.find((q) => q.id === selectedQuestionId);
            const selectedTagIdFromIdentifier = getIdentifierValue(
                selection.xIdentifier,
            );
            const selectedTagId =
                selectedQuestion?.tags?.find(
                    (t) => t.id === selectedTagIdFromIdentifier,
                )?.id ??
                selectedQuestion?.tags?.find((t) => t.value === selection.x)
                    ?.id;

            state.tagId = selectedTagId;
        },
    },
    [SignalsEndpoints.eddyReports.EEPrevalenceByQuestionOverTimeID]: {
        needs: ["selectedIds.question", "selectedIds.module"],
        applyToRequestState: ({ selection, state, source, optionsStore }) => {
            const selectedQuestionId = source.selectedIds?.question;
            const selectedQuestion = optionsStore.orgModules
                .find((module) => module.id === source.selectedIds?.["module"])
                ?.questions.find((q) => q.id === selectedQuestionId);
            const selectedTagId = selectedQuestion?.tags?.find(
                (t) => t.value === selection.series,
            )?.id;

            state.tagId = selectedTagId;
        },
    },
    [SignalsEndpoints.eddyReports.AverageScoreByQuestionID]: {
        needs: ["selectedIds.module"],
        applyToRequestState: ({ selection, state, source, optionsStore }) => {
            const selectedModuleId = source.selectedIds?.module;
            const selectedQuestionId = optionsStore.orgModules
                .find((m) => m.id === selectedModuleId)
                ?.questions.find(
                    (q) => q.shortQuestionText === selection.x,
                )?.id;

            state.questionId = selectedQuestionId;
            state.eddyIdentified = selection.series === "Eddy Identified";
        },
        applyToFilters: ({ selection, filters }) => {
            filters.eddyEffectStatus = selection.series === "Eddy Identified";
        },
    },
    [SignalsEndpoints.eddyReports.AverageScoreByQuestionAndEddyOverTimeID]: {
        needs: ["selectedIds.question"],
        applyToRequestState: ({ selection, state, source }) => {
            state.questionId = source.selectedIds?.question;
            state.eddyIdentified = selection.series === "Eddy Identified";
        },
        applyToFilters: ({ selection, filters }) => {
            filters.eddyEffectStatus = selection.series === "Eddy Identified";
        },
    },
    [SignalsEndpoints.eddyReports.ClassifierAndEddyID]: {
        applyToRequestState: ({ selection, state, optionsStore }) => {
            const classifierId = optionsStore.userClassifiers.find(
                (c) => c.name === selection.x,
            )?.id;

            state.classifierId = classifierId;
            delete (state as any).classifierIds;
            state.eddyIdentified = selection.series === "Eddy Identified";
        },
        applyToFilters: ({ selection, filters, optionsStore }) => {
            const selectedClassifierId = optionsStore.userClassifiers.find(
                (c) => c.name === selection.x,
            )?.id;
            filters.eddyEffectStatus = selection.series === "Eddy Identified";
            return { selectedClassifierId };
        },
    },
    [SignalsEndpoints.trends.ClassifierOverTimeId]: {
        applyToRequestState: ({ selection, state, optionsStore }) => {
            const classifierId = optionsStore.userClassifiers.find(
                (c) => c.name === selection.series,
            )?.id;
            state.classifierId = classifierId;
            delete (state as any).classifierIds;
        },
        applyToFilters: ({ selection, optionsStore }) => {
            const selectedClassifierId = optionsStore.userClassifiers.find(
                (c) => c.name === selection.series,
            )?.id;
            if (selectedClassifierId) {
                return { selectedClassifierId };
            }
        },
    },
    [SignalsEndpoints.trends.HipaaCompliancePrevalenceOverTimeId]: {
        applyToRequestState: ({ selection, state }) => {
            switch (selection.series) {
                case "Compliant":
                    state.hipaaValue = "Compliant";
                    break;
                case "Not Compliant":
                    state.hipaaValue = "Not Compliant";
                    break;
                case "Not Applicable":
                    state.hipaaValue = "Not Applicable";
                    break;
            }
        },
        applyToFilters: ({ selection, filters }) => {
            switch (selection.series) {
                case "Compliant":
                    filters.hipaaCompliance = HipaaCompliance.Compliant;
                    break;
                case "Not Compliant":
                    filters.hipaaCompliance = HipaaCompliance.NotCompliant;
                    break;
                case "Not Applicable":
                    filters.hipaaCompliance = HipaaCompliance.NotApplicable;
                    break;
            }
        },
    },
    [SignalsEndpoints.trends.SEPrevalenceOverTimeID]: {
        applyToRequestState: ({ selection, state }) => {
            const safetyEvent = resolveSafetyEventDrilldownValue(
                selection.series,
            );
            if (safetyEvent) {
                (state as Record<string, unknown>).safetyEvent = safetyEvent;
            }
        },
        applyToFilters: ({ selection, filters }) => {
            if (selection.series) {
                filters.safetyEvent = getSafetyEventFromString(
                    selection.series,
                )!;
            }
        },
    },
    [SignalsEndpoints.trends.QualityScoreOverTimeID]: {},
    [SignalsEndpoints.trends.AverageScoreByModuleOverTimeID]: {
        applyToRequestState: ({ selection, state, source, optionsStore }) => {
            const moduleIdFromSelection =
                selection.seriesIdentifier ||
                source.data?.find(
                    (datum: {
                        x?: string | number | null;
                        grouping?: string | null;
                        grouping_identifier?: string | number | null;
                    }) =>
                        datum.x === selection.x &&
                        datum.grouping === selection.series,
                )?.grouping_identifier;

            if (moduleIdFromSelection) {
                state.moduleId = `${moduleIdFromSelection}`;
                return;
            }

            state.moduleId = [
                ...optionsStore.orgScoredModules,
                ...optionsStore.orgModules,
            ].find(
                (module) =>
                    module.displayName === selection.series ||
                    module.name === selection.series,
            )?.id;
        },
    },
    [SignalsEndpoints.trends.AverageScoreByQuestionOverTimeID]: {
        needs: ["selectedIds.module"],
        applyToRequestState: ({ selection, state, source, optionsStore }) => {
            const selectedModuleId = source.selectedIds?.module;
            const selectedQuestionId = optionsStore.orgModules
                .find((m) => m.id === selectedModuleId)
                ?.questions.find(
                    (q) => q.shortQuestionText === selection.series,
                )?.id;

            state.questionId = selectedQuestionId;
        },
    },
    [SignalsEndpoints.trends.AverageScoreBySkillID]: {
        // No orgModules lookup, unlike the two strategies above: this chart's transform sets
        // X_identifier to the question id, so the clicked bar already carries it. selectDataPoint
        // puts that identifier in xLabel (Google's `x.v` cell) and the short text in `x`; only
        // TreeMap fills a real xIdentifier, hence the usual `xIdentifier ?? xLabel`.
        applyToRequestState: ({ selection, state }) => {
            state.questionId = selection.xIdentifier ?? selection.xLabel;
        },
    },
    [SignalsEndpoints.legacyTopicReports.PrimaryThemesId]: {
        viewDataClassification: "topics",
        needs: ["topics"],
        applyToRequestState: ({ selection, state, source }) => {
            const selectedTopic = source.topics?.find(
                (t) => t.topicLabel === selection.x,
            );
            state.topicId = selectedTopic?.id;
        },
        applyToFilters: ({ selection, filters, selectedTopic, source }) => {
            if (selectedTopic) {
                filters.primaryTopics = [
                    {
                        topicNumber: selectedTopic.topicNumber,
                        topicLabel: selectedTopic.selectedTopic,
                    },
                ];
                filters.containsPrimaryTopics = true;
                return;
            }

            const topicFromSeries = source.topics?.find(
                (t) => t.topicLabel === selection.x,
            );
            if (!topicFromSeries) return;

            filters.topics = [
                {
                    id: topicFromSeries.id,
                    topicLabel: topicFromSeries.topicLabel,
                },
            ];
            filters.containsTopics = true;
        },
    },
    [SignalsEndpoints.legacyTopicReports.ThemesOverTimeId]: {
        viewDataClassification: "topics",
        needs: ["topics"],
        applyToRequestState: ({ selection, state, source }) => {
            const selectedTopic = source.topics?.find(
                (t) => t.topicLabel === selection.series,
            );
            state.topicId = selectedTopic?.id;
        },
        applyToFilters: ({ selection, filters, selectedTopic, source }) => {
            if (selectedTopic) {
                filters.primaryTopics = [
                    {
                        topicNumber: selectedTopic.topicNumber,
                        topicLabel: selectedTopic.selectedTopic,
                    },
                ];
                filters.containsPrimaryTopics = true;
                return;
            }

            const selected = source.topics?.find(
                (t) => t.topicLabel === selection.series,
            );
            if (!selected) return;

            filters.topics = [
                {
                    id: selected.id,
                    topicLabel: selected.topicLabel,
                },
            ];
            filters.containsTopics = true;
        },
    },
    [SignalsEndpoints.legacyTopicReports.ThemeAndEEID]: {
        viewDataClassification: "topics",
        needs: ["topics"],
        applyToRequestState: ({ selection, state, source }) => {
            const selectedTopic = source.topics?.find(
                (t) => t.topicLabel === selection.x,
            );
            state.topicId = selectedTopic?.id;
            state.eddyIdentified = selection.series === "Eddy Identified";
        },
        applyToFilters: ({ selection, filters, selectedTopic, source }) => {
            if (selectedTopic) {
                filters.primaryTopics = [
                    {
                        topicNumber: selectedTopic.topicNumber,
                        topicLabel: selectedTopic.selectedTopic,
                    },
                ];
                filters.containsPrimaryTopics = true;
                return;
            }

            const foundTopic = source.topics?.find(
                (t) => t.topicLabel === selection.x,
            );
            if (!foundTopic) return;

            filters.eddyEffectStatus = selection.series === "Eddy Identified";
            filters.topics = [
                { id: foundTopic.id, topicLabel: foundTopic.topicLabel },
            ];
            filters.containsTopics = true;
        },
    },
    // The Theme Prevalence Cards mirror their TopicPrevalence* predecessors, but read their
    // categories from `source.themes` and write into the `themes` filter throughout. The
    // predecessors' selectedTopic branch has no analogue on the way in: these Cards drill a Theme
    // into its Drivers, so a selection arriving from that aggregation is a Driver, handled below.
    [SignalsEndpoints.themes.ThemePrevalenceId]: {
        needs: ["themes"],
        applyToRequestState: ({ selection, state, source }) => {
            const selectedTheme = source.themes?.find(
                (t) => t.themeLabel === selection.x,
            );
            state.themeId = selectedTheme?.id;
        },
        applyToFilters: ({ selection, filters, selectedDriver, source }) => {
            const themeFromSeries = source.themes?.find(
                (t) => t.themeLabel === selection.x,
            );
            if (themeFromSeries) {
                filters.themes = [
                    {
                        id: themeFromSeries.id,
                        themeLabel: themeFromSeries.themeLabel,
                    },
                ];
                filters.containsThemes = true;
            }

            if (selectedDriver) {
                filters.drivers = [
                    {
                        driverIndex: selectedDriver.driverIndex,
                        driverName: selectedDriver.selectedDriver,
                    },
                ];
                filters.containsDrivers = true;
            }
        },
    },
    [SignalsEndpoints.themes.ThemePrevalenceOverTimeId]: {
        needs: ["themes"],
        applyToRequestState: ({ selection, state, source }) => {
            const selectedTheme = source.themes?.find(
                (t) => t.themeLabel === selection.series,
            );
            state.themeId = selectedTheme?.id;
        },
        applyToFilters: ({ selection, filters, selectedDriver, source }) => {
            const selected = source.themes?.find(
                (t) => t.themeLabel === selection.series,
            );
            if (selected) {
                filters.themes = [
                    {
                        id: selected.id,
                        themeLabel: selected.themeLabel,
                    },
                ];
                filters.containsThemes = true;
            }

            if (selectedDriver) {
                filters.drivers = [
                    {
                        driverIndex: selectedDriver.driverIndex,
                        driverName: selectedDriver.selectedDriver,
                    },
                ];
                filters.containsDrivers = true;
            }
        },
    },
    [SignalsEndpoints.themes.ThemePrevalenceAndEEId]: {
        needs: ["themes"],
        applyToRequestState: ({ selection, state, source }) => {
            const selectedTheme = source.themes?.find(
                (t) => t.themeLabel === selection.x,
            );
            state.themeId = selectedTheme?.id;
            state.eddyIdentified = selection.series === "Eddy Identified";
        },
        applyToFilters: ({ selection, filters, selectedDriver, source }) => {
            const foundTheme = source.themes?.find(
                (t) => t.themeLabel === selection.x,
            );
            filters.eddyEffectStatus = selection.series === "Eddy Identified";
            if (foundTheme) {
                filters.themes = [
                    { id: foundTheme.id, themeLabel: foundTheme.themeLabel },
                ];
                filters.containsThemes = true;
            }

            if (selectedDriver) {
                filters.drivers = [
                    {
                        driverIndex: selectedDriver.driverIndex,
                        driverName: selectedDriver.selectedDriver,
                    },
                ];
                filters.containsDrivers = true;
            }
        },
    },
    [SignalsEndpoints.contacts.ContactTypePrevalenceId]: {
        applyToRequestState: ({
            selection,
            state,
            applicationFilterValuesStore,
        }) => {
            state.contactType =
                applicationFilterValuesStore.mappedCustomerTypes.find(
                    (i) => i.name === selection.x,
                )?.sourceName;
        },
        applyToFilters: ({
            selection,
            filters,
            applicationFilterValuesStore,
        }) => {
            const mappedContactLabel =
                applicationFilterValuesStore.mappedCustomerTypes.find(
                    (i) => i.name === selection.x,
                )?.sourceName;

            if (mappedContactLabel) {
                filters.contactTypes = [mappedContactLabel];
            }
        },
    },
    [SignalsEndpoints.contacts.ContactTypePrevalenceOverTimeId]: {
        applyToRequestState: ({
            selection,
            state,
            applicationFilterValuesStore,
        }) => {
            state.contactType =
                applicationFilterValuesStore.mappedCustomerTypes.find(
                    (i) => i.name === selection.series,
                )?.sourceName;
        },
        applyToFilters: ({
            selection,
            filters,
            applicationFilterValuesStore,
        }) => {
            const mappedContactLabel =
                applicationFilterValuesStore.mappedCustomerTypes.find(
                    (i) => i.name === selection.series,
                )?.sourceName;

            if (mappedContactLabel) {
                filters.contactTypes = [mappedContactLabel];
            }
        },
    },
    [SignalsEndpoints.contacts.EddyByContactTypeOverTimeId]: {
        applyToRequestState: ({
            selection,
            state,
            applicationFilterValuesStore,
        }) => {
            state.contactType =
                applicationFilterValuesStore.mappedCustomerTypes.find(
                    (i) => i.name === selection.series,
                )?.sourceName;
        },
        applyToFilters: ({
            selection,
            filters,
            applicationFilterValuesStore,
        }) => {
            const mappedContactLabel =
                applicationFilterValuesStore.mappedCustomerTypes.find(
                    (i) => i.name === selection.series,
                )?.sourceName;
            if (!mappedContactLabel) return;

            filters.contactTypes = [mappedContactLabel];
            filters.eddyEffectStatus = true;
        },
    },
    [SignalsEndpoints.contacts.ContactTypePrevalenceAndEEId]: {
        applyToRequestState: ({
            selection,
            state,
            applicationFilterValuesStore,
        }) => {
            state.contactType =
                applicationFilterValuesStore.mappedCustomerTypes.find(
                    (i) => i.name === selection.x,
                )?.sourceName;
            state.eddyIdentified = selection.series === "Eddy Identified";
        },
        applyToFilters: ({
            selection,
            filters,
            applicationFilterValuesStore,
        }) => {
            const mappedContactLabel =
                applicationFilterValuesStore.mappedCustomerTypes.find(
                    (i) => i.name === selection.x,
                )?.sourceName;
            if (!mappedContactLabel) return;

            filters.contactTypes = [mappedContactLabel];
            filters.eddyEffectStatus = selection.series === "Eddy Identified";
        },
    },
    [SignalsEndpoints.additionalReports.ModelResultsCountId]: {
        applyToRequestState: ({ selection, state }) => {
            state.result = selection.x;
        },
        applyToFilters: ({ selection, filters, state, optionsStore }) => {
            if (!state.llmClassifierId) return;
            const classifierId = state.llmClassifierId as string;
            const selectedLLMClassifier = optionsStore.llmClassifiers.find(
                (i) => i.id === classifierId,
            );

            if (selection.x) {
                const existing = filters.modelResults ?? [];
                filters.modelResults = [
                    ...existing,
                    {
                        id: classifierId,
                        name: selectedLLMClassifier?.name ?? "",
                        results: [selection.x as string],
                    },
                ];
            }
        },
    },
    [SignalsEndpoints.additionalReports.ModelResultsOverTimeId]: {
        applyToRequestState: ({ selection, state }) => {
            state.result = selection.series;
        },
        applyToFilters: ({ selection, filters, state, optionsStore }) => {
            const classifierId = state.llmClassifierId as string;
            if (!classifierId || !selection.series) return;
            const selectedLLMClassifier = optionsStore.llmClassifiers.find(
                (i) => i.id === classifierId,
            );
            const existing = filters.modelResults ?? [];
            filters.modelResults = [
                ...existing,
                {
                    id: classifierId,
                    name: selectedLLMClassifier?.name ?? "",
                    results: [selection.series],
                },
            ];
        },
    },
    [SignalsEndpoints.additionalReports.GroupedModelResultsId]:
        groupedModelResultsDrilldown,
    [SignalsEndpoints.additionalReports.PublishedGroupedModelResultsId]:
        groupedModelResultsDrilldown,
    [SignalsEndpoints.additionalReports.JourneyStageCountId]: {
        applyToRequestState: ({ selection, state }) => {
            state.stage = selection.x;
        },
        applyToFilters: ({ selection, filters }) => {
            const stage = resolveJourneyStage(selection.x);
            if (stage) {
                filters.patientJourneyStages = [stage];
            }
        },
    },
    [SignalsEndpoints.additionalReports.JourneyStageAndEddyEffectId]: {
        applyToRequestState: ({ selection, state }) => {
            state.stage = selection.x;
            state.eddyIdentified = selection.series === "Eddy Identified";
        },
        applyToFilters: ({ selection, filters }) => {
            const stage = resolveJourneyStage(selection.x);
            if (!stage) return;
            filters.eddyEffectStatus = selection.series === "Eddy Identified";
            filters.patientJourneyStages = [stage];
        },
    },
    [SignalsEndpoints.additionalReports.ModuleResultsCountId]: {
        needs: ["data"],
        applyToRequestState: ({ selection, state, source, optionsStore }) => {
            const dataPoint = source.data?.find(
                (v) => v.x === selection.x && v.grouping === selection.series,
            );
            if (!dataPoint) return;

            let moduleId: string | undefined;
            for (const module of optionsStore.orgModules) {
                for (const question of module.questions) {
                    if (question.id === dataPoint.x_identifier) {
                        moduleId = module.id;
                        break;
                    }
                }
                if (moduleId) break;
            }

            state.moduleId = moduleId;
            state.questionId = dataPoint.x_identifier;
            state.tagId = dataPoint.grouping_identifier;
        },
    },
    [SignalsEndpoints.additionalReports.CallDirectionId]: {
        applyToRequestState: ({ selection, state }) => {
            state.callDirection = selection.x;
        },
        applyToFilters: ({ selection, filters }) => {
            if (!selection.x) return;
            filters.callDirections = [CallDirection[selection.x]];
        },
    },
    [SignalsEndpoints.additionalReports.ConversationsOverTimeId]: {},
    [SignalsEndpoints.additionalReports.PublishedModelResultsOverTimeId]: {
        applyToFilters: ({ selection, filters, source, optionsStore }) => {
            const dataEndpoint = source.dataEndpoint;
            if (!dataEndpoint || !selection.series) return;
            const classifierId =
                getPublishedModelResultsClassifierIdFromEndpoint(dataEndpoint);
            if (!classifierId) return;
            const selectedLLMClassifier = optionsStore.llmClassifiers.find(
                (i) => i.id === classifierId,
            );
            filters.modelResults = [
                {
                    id: classifierId,
                    name: selectedLLMClassifier?.name ?? "",
                    results: [selection.series],
                },
            ];
        },
        applyToRequestState: ({ selection, state }) => {
            state.result = selection.series;
        },
    },
    [SignalsEndpoints.additionalReports.PublishedModelResultsCountId]: {
        applyToRequestState: ({ selection, state }) => {
            state.result = selection.x;
        },
        applyToFilters: ({ selection, filters, source, optionsStore }) => {
            const dataEndpoint = source.dataEndpoint;
            if (!dataEndpoint) return;
            const classifierId =
                getPublishedModelResultsClassifierIdFromEndpoint(dataEndpoint);
            if (!classifierId) return;
            const selectedLLMClassifier = optionsStore.llmClassifiers.find(
                (i) => i.id === classifierId,
            );

            if (selection.x) {
                filters.modelResults = [
                    {
                        id: classifierId,
                        name: selectedLLMClassifier?.name ?? "",
                        results: [selection.x as string],
                    },
                ];
            }
        },
    },

    // Insight (small) cards. Each card's drilldown returns the conversations its stat was computed
    // from. The View Data request is scoped server side; what these restate is the same scope in
    // application-filter terms, so View in Conversations and Ask KBo stay in step with the grid.
    [SignalsEndpoints.smallCards.TotalConversationsId]: {
        // The card counts every conversation the dashboard filters already describe, so there is
        // nothing further to narrow. Declared rather than left out so the card reads as deliberate.
    },
    [SignalsEndpoints.smallCards.AvgCallDurationId]: {
        applyToFilters: ({ filters, source }) => {
            // Call duration is an audio-only measure, and the card's query scopes to it the same way.
            filters.mediaTypes = [MediaType.Audio];

            const eddyPresence = resolveEddyPresence(source);
            if (eddyPresence === "WithEddy") {
                filters.eddyEffectStatus = true;
            } else if (eddyPresence === "WithoutEddy") {
                filters.eddyEffectStatus = false;
            }
        },
    },
    [SignalsEndpoints.smallCards.ConversationsWithAnEddyId]: {
        applyToFilters: ({ filters }) => {
            // The stat is the share of conversations carrying an Eddy, so the drilldown is that
            // numerator rather than the population it was measured against.
            filters.eddyEffectStatus = true;
        },
    },
    [SignalsEndpoints.smallCards.AvgModuleScoreId]: {
        applyToFilters: ({ filters }) => {
            // A module score only exists on an evaluated conversation, so this narrows to the
            // population the average was taken over. The card's module category cannot be carried
            // further than that here: Conversations scopes module scores one module at a time and
            // ANDs them, which would demand a conversation scored on every module in the category
            // rather than any one of them. View Data is unaffected -- the server resolves the
            // category's modules itself.
            filters.evaluationTypes = [
                EvaluationType.AIEvaluated,
                EvaluationType.HumanEvaluated,
            ];
        },
    },
};
